/* ============================================================
   STORIES — segmenten die vanzelf doorlopen.

   Elk segment heeft een eigen duur; een video bepaalt zijn duur zelf. De
   balkjes bovenin lopen mee. Pauzeren, geluid en vorige/volgende zitten in de
   balk erboven.

   prefers-reduced-motion: geen automatisch verloop. Het eerste segment blijft
   staan en je bladert zelf met de pijlen — alle inhoud blijft bereikbaar.
   ============================================================ */

const stilStaan = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class StoryList extends HTMLElement {
  connectedCallback() {
    this.segmenten = [...this.querySelectorAll('.story-list__segment')];
    this.balken = [...this.querySelectorAll('[data-story-bar] .story-bar__fill')];
    if (!this.segmenten.length) return;

    this.speelKnop = this.querySelector('[data-play-btn]');
    this.geluidKnop = this.querySelector('[data-mute-btn]');
    this.index = 0;
    this.speelt = false;
    this.gedempt = true;

    this.querySelector('[data-story-prev]')?.addEventListener('click', () => this.ga(this.index - 1));
    this.querySelector('[data-story-next]')?.addEventListener('click', () => this.ga(this.index + 1));
    this.speelKnop?.addEventListener('click', () => (this.speelt ? this.pauzeer() : this.speel()));
    this.geluidKnop?.addEventListener('click', () => this.wisselGeluid());

    this.toon(0);

    // pas starten als hij in beeld komt, en pauzeren zodra hij eruit gaat
    this.kijker = new IntersectionObserver((rijen) => {
      for (const rij of rijen) {
        if (rij.isIntersecting && !stilStaan()) this.speel();
        else this.pauzeer();
      }
    }, { threshold: 0.5 });
    this.kijker.observe(this);
    this.tekenKnoppen();
  }

  disconnectedCallback() { this.kijker?.disconnect(); this.stopTeller(); }

  get huidig() { return this.segmenten[this.index]; }
  get video() { return this.huidig?.querySelector('.story-list__variant:not([hidden]) video, video'); }

  toon(i) {
    this.index = (i + this.segmenten.length) % this.segmenten.length;
    this.segmenten.forEach((s, n) => s.toggleAttribute('hidden', n !== this.index));
    this.balken.forEach((b, n) => {
      b.style.transition = 'none';
      b.style.transform = n < this.index ? 'scaleX(1)' : 'scaleX(0)';
    });
    this.segmenten.forEach((s) => {
      const v = s.querySelector('video');
      if (v) { v.pause(); v.currentTime = 0; }
    });
  }

  ga(i) { this.pauzeer(); this.toon(i); if (!stilStaan()) this.speel(); }

  speel() {
    if (this.speelt) return;
    this.speelt = true;
    const v = this.video;
    const duur = v && v.duration ? v.duration : Number(this.huidig.dataset.duration || 6);
    if (v) { v.muted = this.gedempt; v.play().catch(() => {}); }
    const balk = this.balken[this.index];
    if (balk) {
      balk.style.transition = `transform ${duur}s linear`;
      requestAnimationFrame(() => { balk.style.transform = 'scaleX(1)'; });
    }
    this.timer = setTimeout(() => this.ga(this.index + 1), duur * 1000);
    this.tekenKnoppen();
  }

  pauzeer() {
    this.speelt = false;
    this.stopTeller();
    const v = this.video;
    if (v) v.pause();
    const balk = this.balken[this.index];
    if (balk) {
      // de balk bevriezen op de plek waar hij nu staat
      const nu = getComputedStyle(balk).transform;
      balk.style.transition = 'none';
      balk.style.transform = nu === 'none' ? 'scaleX(0)' : nu;
    }
    this.tekenKnoppen();
  }

  stopTeller() { clearTimeout(this.timer); }

  wisselGeluid() {
    this.gedempt = !this.gedempt;
    const v = this.video;
    if (v) v.muted = this.gedempt;
    this.tekenKnoppen();
  }

  tekenKnoppen() {
    const zet = (sel, aan) => { const e = this.querySelector(sel); if (e) e.hidden = !aan; };
    zet('[data-icon-play]', !this.speelt);
    zet('[data-icon-pause]', this.speelt);
    zet('[data-icon-muted]', this.gedempt);
    zet('[data-icon-unmuted]', !this.gedempt);
    this.speelKnop?.setAttribute('aria-label', this.speelt ? this.dataset.labelPause : this.dataset.labelPlay);
    this.geluidKnop?.setAttribute('aria-label', this.gedempt ? this.dataset.labelUnmute : this.dataset.labelMute);
  }
}

for (const [naam, klasse] of [
  ['c-story-list', StoryList],
  ['c-info-card', class extends HTMLElement {}],
]) {
  if (!customElements.get(naam)) customElements.define(naam, klasse);
}
